import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { BehaviorSubject, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { Specialty } from "../models/specialty.model";
import { SubSpecialty } from "../models/sub-specialty.model";


@Injectable({
    providedIn: 'root',
})
export class SpecialtiesCollectionService {
    private ALL_SUB_SPECIALTIES = 'ALL SPECIALTIES';

    private specialtyArray = new BehaviorSubject([]);
    currentSpecialtyArray = this.specialtyArray.asObservable();

    private subSpecialtyArray = new BehaviorSubject([]);
    currentSubSpecialtyArray = this.subSpecialtyArray.asObservable();

    private specialtyInfo = new BehaviorSubject(null);
    currentSpecialtyInfo = this.specialtyInfo.asObservable();

    constructor(
        private angularFirestore: AngularFirestore,
        private angularFunctions: AngularFireFunctions,
        private afAuth: AngularFireAuth
    ) { }

    getSpecialtyListAsync() {
        return this.afAuth.authState.pipe(
            switchMap(user => {
                if (user) {
                    return this.angularFirestore
                        .collection<Specialty>('Specialty', ref =>
                            ref.orderBy('specialty_name')
                        )
                        .valueChanges({ idField: 'id' });
                } else {
                    return of(null);
                }
            })
        );
    }

    getSubSpecialtyListAsync() {
        return this.afAuth.authState.pipe(
            switchMap(user => {
                if (user) {
                    return this.angularFirestore
                        .collection<SubSpecialty>('SubSpecialty')
                        .valueChanges({ idField: 'id' });
                } else {
                    return of(null);
                }
            })
        );
    }

    async saveNewSubSpecialty(subSpecialtyData: any) {
        try {
            let newSubSpecialty = subSpecialtyData;

            const subSpecialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');

            // Add new sub specialty to colelction
            await subSpecialtyCollection.ref
                .add(JSON.parse(JSON.stringify(newSubSpecialty)))
                .then(function (docRef) {
                    newSubSpecialty.id = docRef.id
                })
                .catch(err => console.log("saveNewSpecialty firebaseSubSpecialtyCollection save 1 error: " + err));

            if (newSubSpecialty.id == null) {
                return null;
            }

            return true;
        } catch (error) {
            console.log(error);
        }
    }

    async removeSubSpecialty(subSpecialtyData: any) {
        try {
            let subSpecialty = subSpecialtyData;

            // Call cloud function editSubSpecialty and send id and new sub specialty name
            const callable = this.angularFunctions.httpsCallable('removeSubSpecialty');
            callable({
                id: subSpecialty.id
            })
                .toPromise()
                .then(result => {
                    console.log(result);
                }).catch(err => console.log("removeSubSpecialty callable error: " + err));


            // remove sub specialty from collection
            const subSpecialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');
            await subSpecialtyCollection.ref
                .doc(subSpecialty.id)
                .delete()
                .catch(err => console.log("removeSubSpecialty firebaseSubSpecialtyCollection save 1 error: " + err));

            return true;
        } catch (error) {
            console.log('removeSubSpecialty error', error);
        }
    }

    async removeSpecialty(specialtyData: any) {
        try {
            let specialty = specialtyData;

            // Call cloud function editSubSpecialty and send id and new sub specialty name
            const callable = this.angularFunctions.httpsCallable('removeSpecialty');
            callable({
                id: specialty.id
            })
                .toPromise()
                .then(result => {
                    console.log(result);
                }).catch(err => console.log("removeSpecialty callable error: " + err));


            // remove sub specialty from collection
            const specialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');
            await specialtyCollection.ref
                .doc(specialty.id)
                .delete()
                .catch(err => console.log("removeSpecialty firebaseSubSpecialtyCollection save 1 error: " + err));

            return true;
        } catch (error) {
            console.log('removeSpecialty error', error);
        }
    }

    async updateSpecialty(specialtyData: any) {
        try {
            let updatedSpecialty = specialtyData;

            const specialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');

            // Update sub speciaty in collection, only update the sub_specialty_name field
            await specialtyCollection.ref
                .doc(updatedSpecialty.id)
                .update({
                    specialty_name: updatedSpecialty.specialty_name
                })
                .catch(err => console.log("updateSpecialty firebaseSpecialtyCollection save 1 error: " + err));

            // Call cloud function editSpecialty and send id and new sub specialty name
            const callable = this.angularFunctions.httpsCallable('editSpecialty');
            callable({
                id: updatedSpecialty.id,
                specialty_name: updatedSpecialty.specialty_name
            })
                .toPromise()
                .then(result => {
                    console.log(result);
                }).catch(err => console.log("updateSpecialty callable error: " + err));

            return true;
        } catch (error) {
            console.log(error);
        }
    }

    async updateSpecialtyStatus(specialtyData: any) {
        try {
            let updatedSpecialty = specialtyData;

            const specialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');

            // Update sub speciaty in collection, only update the sub_specialty_name field
            await specialtyCollection.ref
                .doc(updatedSpecialty.id)
                .update({
                    disabled: updatedSpecialty.disabled
                })
                .catch(err => console.log("updateSpecialtyStatus save 1 error: " + err));

            return true;
        } catch (error) {
            console.log(error);
        }
    }



    async updateSubSpecialty(subSpecialtyData: any) {
        try {
            let newSubSpecialty = subSpecialtyData;

            const subSpecialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');

            // Update sub speciaty in collection, only update the sub_specialty_name field
            await subSpecialtyCollection.ref
                .doc(newSubSpecialty.id)
                .update({
                    sub_specialty_name: newSubSpecialty.sub_specialty_name
                })
                .catch(err => console.log("updateSubSpecialty firebaseSubSpecialtyCollection save 1 error: " + err));

            // Call cloud function editSubSpecialty and send id and new sub specialty name
            const callable = this.angularFunctions.httpsCallable('editSubSpecialty');
            callable({
                id: newSubSpecialty.id,
                sub_specialty_name: newSubSpecialty.sub_specialty_name
            })
                .toPromise()
                .then(result => {
                    console.log(result);
                }).catch(err => console.log("updateSubSpecialty callable error: " + err));

            return true;
        } catch (error) {
            console.log(error);
        }
    }

    async saveNewSpecialty(specialtyData: any) {
        try {
            let newSpecialty: any = {
                specialty_name: specialtyData.specialty_name,
                disabled: false
            }

            // Setup collections if null
            const specialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');
            const subSpecialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');
            const expertSubSpecialtyCollection: AngularFirestoreCollection = this.angularFirestore.collection('ExpertSubSpecialties');
            const weeklyDefaultCollection: AngularFirestoreCollection = this.angularFirestore.collection('OnCallWeeklySchedule');

            // Add specialty
            await specialtyCollection.ref
                .add(JSON.parse(JSON.stringify(newSpecialty)))
                .then(function (docRef) {
                    newSpecialty.id = docRef.id
                })
                .catch(err => console.log("saveNewSpecialty firebaseSpecialtyCollection save 1 error: " + err));


            // If error or null, return
            if (newSpecialty.id == null) {
                return null;
            }

            // Create sub specialty object using new specialty data
            let newSubSpecialty: any = {
                general_sub_specialty: true,
                specialty_id: newSpecialty.id,
                sub_specialty_name: specialtyData.general_sub_specialty
            }

            // Add new sub specialty to colelction
            await subSpecialtyCollection.ref
                .add(JSON.parse(JSON.stringify(newSubSpecialty)))
                .then(function (docRef) {
                    newSubSpecialty.id = docRef.id
                })
                .catch(err => console.log("saveNewSpecialty firebaseSubSpecialtyCollection save 1 error: " + err));

            // If new sub specialty was not created successfully
            // Delete specialty created and then return
            if (newSubSpecialty.id == null) {
                await specialtyCollection.ref
                    .doc(newSpecialty.id)
                    .delete()
                    .then(res => {
                        console.log('Specialty deleted successfully');
                    })
                    .catch(err => console.log("saveNewSpecialty firebaseSpecialtyCollection delete 1 error: " + err));
                return null;
            }

            // Create expert sub specialty object using new sub specialty data
            let newExpertSubSpecialty: any = {
                expert_id: specialtyData.expert.id,
                general_sub_specialty: true,
                specialty: newSpecialty.specialty_name,
                specialty_id: newSpecialty.id,
                sub_specialty: specialtyData.general_sub_specialty,
                sub_specialty_id: newSubSpecialty.id
            }

            // Add new expert sub specialty to colelction
            await expertSubSpecialtyCollection.ref
                .add(JSON.parse(JSON.stringify(newExpertSubSpecialty)))
                .then(function (docRef) {
                    newExpertSubSpecialty.id = docRef.id
                })
                .catch(err => console.log("saveNewSpecialty firebaseExpertSubSpecialtiesCollection save 1 error: " + err));

            // If new sub specialty was not created successfully
            // Delete specialty created
            // Delete sub specialty created
            // Return
            if (newSubSpecialty.id == null) {
                await specialtyCollection.ref
                    .doc(newSpecialty.id)
                    .delete()
                    .then(res => {
                        console.log('Specialty deleted successfully');
                    })
                    .catch(err => console.log("saveNewSpecialty firebaseSpecialtyCollection delete 1 error: " + err));

                await subSpecialtyCollection.ref
                    .doc(newSubSpecialty.id)
                    .delete()
                    .then(res => {
                        console.log('Sub Specialty deleted successfully');
                    })
                    .catch(err => console.log("saveNewSpecialty firebaseSubSpecialtyCollection delete 1 error: " + err));
                return null;
            }

            let newWeeklySchedule = {
                specialty_id: newSpecialty.id,
                week: {
                    0: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    1: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    2: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    3: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    4: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    5: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    },
                    6: {
                        first_name: specialtyData.expert.first_name,
                        last_name: specialtyData.expert.last_name,
                        id: specialtyData.expert.id
                    }
                }
            }

            await weeklyDefaultCollection.ref
                .add(JSON.parse(JSON.stringify(newWeeklySchedule)))
                .catch(err => console.log("saveNewSpecialty firebaseWeeklyDefaultCollection save 1 error: " + err));

            const callable = this.angularFunctions.httpsCallable('createOnCallScheduleNewSpecialty');

            callable({
                expert_id: specialtyData.expert.id,
                first_name: this.getCapitalizedWord(specialtyData.expert.first_name),
                last_name: this.getCapitalizedWord(specialtyData.expert.last_name),
                specialty_id: newSpecialty.id,
            })
                .toPromise()
                .then((result) => console.log('saveNewSpecialty call on call create', result))
                .catch((error) => console.log('saveNewSpecialty call on call create', error));

            // TODO CHECK
            // this.getSpecialtyList();

            return true;
        } catch (error) {
            console.log(error);
        }
    }

    async getSubSpecialtyBySpecialtyID(id: string) {
        try {
            let subSpecialtyList = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');

            await collection.ref
                .where('specialty_id', '==', id)
                .orderBy('sub_specialty_name', 'asc')
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        subSpecialtyList.push(docData)
                    });
                }).catch(err => console.log("getSubSpecialtyList query 1 error: " + err));

            this.updateSubSpecialtyList(subSpecialtyList);
            return subSpecialtyList;
        } catch (error) {
            console.log(error);
        }
    }

    async getSpecialtyInfo(specialtyID: string) {
        try {
            let specialty = null;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');

            await collection.ref
                .doc(specialtyID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    specialty = docData;
                }).catch(err => console.log("getSpecialtyInfo query 1 error: " + err));

            this.updateSpecialtyInfo(specialty);
            return specialty;
        } catch (error) {
            console.log(error);
        }
    }

    async getActiveSpecialtyList() {
        try {
            let specialtyList = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');

            await collection.ref
                .where('disabled', '==', false)
                .orderBy('specialty_name')
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        specialtyList.push(docData)
                    });
                }).catch(err => console.log("getSpecialtyList query 1 error: " + err));

            this.updateSpecialtyList(specialtyList);
            return specialtyList;
        } catch (error) {
            console.log(error);
        }
    }

    async getSpecialtyList() {
        try {
            let specialtyList = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Specialty');

            await collection.ref
                .orderBy('specialty_name')
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        specialtyList.push(docData)
                    });
                }).catch(err => console.log("getSpecialtyList query 1 error: " + err));

            this.updateSpecialtyList(specialtyList);
            return specialtyList;
        } catch (error) {
            console.log(error);
        }
    }

    async getSubSpecialtyList() {
        try {
            let subSpecialtyList = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('SubSpecialty');

            await collection.ref
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        subSpecialtyList.push(docData)
                    });
                }).catch(err => console.log("getSubSpecialtyList query 1 error: " + err));

            this.updateSubSpecialtyList(subSpecialtyList);
            return subSpecialtyList;
        } catch (error) {
            console.log(error);
        }
    }

    updateSpecialtyInfo(specialtyInfo) {
        this.specialtyInfo.next(specialtyInfo);
    }

    updateSubSpecialtyList(subSpecialtyList: any) {
        let subSpecialtyListArrayTemp: SubSpecialty[] = [];
        for (let i = 0; i < subSpecialtyList.length; i++) {
            let subSpecialty = new SubSpecialty();
            subSpecialty.id = subSpecialtyList[i].id || subSpecialtyList[i].id;
            subSpecialty.sub_specialty_name = subSpecialtyList[i]["sub_specialty_name"];
            subSpecialty.specialty_id = subSpecialtyList[i]["specialty_id"];
            subSpecialty.general_sub_specialty = subSpecialtyList[i]["general_sub_specialty"];
            if (subSpecialtyList[i]["general_sub_specialty"]) {
                subSpecialtyListArrayTemp.unshift(subSpecialty);
            } else {
                subSpecialtyListArrayTemp.push(subSpecialty);
            }
        }
        this.subSpecialtyArray.next(subSpecialtyListArrayTemp);
    }

    public getCapitalizedWord(str: string) {
        let updatedString = str;
        updatedString = updatedString.toLowerCase()
            .split(' ')
            .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
            .join(' ');

        return updatedString;
    }

    updateSpecialtyList(specialtyList: any) {
        let specialtyArrayTemp: Specialty[] = [];
        let allSpecialty = new Specialty();
        allSpecialty.id = '0';
        allSpecialty.specialty_name = this.ALL_SUB_SPECIALTIES
        specialtyArrayTemp.push(allSpecialty);

        for (let i = 0; i < specialtyList.length; i++) {
            let specialty = new Specialty();
            specialty.id = specialtyList[i].id || specialtyList[i].id;
            specialty.specialty_name = specialtyList[i].specialty_name;
            specialty.disabled = specialtyList[i].disabled;
            specialtyArrayTemp.push(specialty);
        }
        specialtyArrayTemp.sort(function (a, b) {
            if (a.specialty_name < b.specialty_name) { return -1; }
            if (a.specialty_name > b.specialty_name) { return 1; }
            return 0;
        })
        this.specialtyArray.next(specialtyArrayTemp);
    }
}